<template>
  <div>
    <div v-if="loading">
      <BaseLoader />
    </div>
    <div v-else class="page text-black">
      <transition name="fade">
        <Header v-if="showHeader" class="bg-white" />
      </transition>
      <transition name="fade">
        <Hero v-if="showHero && isLink" />
      </transition>
      <transition name="fade">
        <main v-if="showContent && !isLink" class="pt-32 pb-16 space-y-16">
      <section id="bokvalitet-intro" class="content-wrapper py-10">
        <div class="flex flex-col-reverse lg:grid gap-12 lg:grid-cols-12 items-start">
          <div class="lg:col-span-5">
            <h2 class="mb-12 text-3xl font-semibold text-black">Et dialogverktøy om bokvalitet</h2>
            <p class="mb-6 text-xl leading-relaxed text-gray-800">
              Bokvalitetsrosen er et dialogverktøy som hjelper eiendomsutviklere, arkitekter og
              kommuner med å definere og dokumentere bokvalitet. Målet er bedre planprosesser,
              tydeligere forventninger og færre misforståelser.
            </p>
            <p class="mb-3 text-xl text-gray-900">Verktøyet egner seg godt for</p>
            <ul class="list-disc space-y-2 pl-6 text-xl text-gray-900">
              <li>boligutviklere som vil vise kvalitetsambisjoner</li>
              <li>arkitekter som vil synliggjøre flere aspekter av bokvalitet</li>
              <li>kommuner som vurderer boligprosjekter og ønsker et strukturert grunnlag for dialog</li>
              <li>tverrfaglige prosjektteam med behov for et felles språk</li>
            </ul>
          </div>
          <div class="rounded-xl p-0 overflow-hidden lg:col-span-7">
            <img
              :src="bokvalitetLandingPageIllustration"
              alt="Illustrasjon av bokvalitet i bymiljo"
              class="h-auto w-full object-cover"
            >
          </div>
        </div>
      </section>

      <section id="hva-sier-brukerne" class="content-wrapper py-10">
        <h2 class="mb-12 text-3xl font-semibold text-black">Hva sier bransjen?</h2>
        <div class="flex flex-col lg:grid items-start gap-8 lg:grid-cols-12">
          <div class="hidden lg:block lg:col-span-5">
            <img
              src="@/assets/images/bokvalitet_logo_icon.png"
              alt="Bokvalitetsrosen ikon"
              class="h-auto w-full object-contain"
            >
          </div>
          <div class="lg:col-span-7">
            <div class="grid gap-4 md:grid-cols-2">
              <BaseCard class="h-full">
                <p class="mb-1 text-lg font-semibold">Christian Berg</p>
                <p class="mb-3 text-sm text-gray-700">Regionsjef Oslo, Nåbo boligutvikling</p>
                <p class="mb-3 text-lg font-semibold leading-relaxed">"Bokvalitetsrosen bidrar til at vi kan legge frem noe som belyser bredden av bokvalitet på en helt annen måte enn antall kvadratmeter."</p>
                <p class="text-sm leading-relaxed">Bokvalitet er jo gjerne spissa diskusjoner, som leilighetsstørrelser osv. Med Bokvalitetsrosen kan man reflektere over mange andre kriterier som faktisk utgjør god bokvalitet.</p>
              </BaseCard>
              <BaseCard class="h-full">
                <p class="mb-1 text-lg font-semibold">Tom Sletner</p>
                <p class="mb-3 text-sm text-gray-700">Arkitekt og partner, LPO</p>
                <p class="mb-3 text-lg font-semibold leading-relaxed">"Vi må hele tiden snakke sammen og løfte frem alle kvaliteter i et prosjekt."</p>
                <p class="text-sm leading-relaxed">En common base for å diskutere kvalitet er svært nyttig – også i de interne diskusjonene. Det er viktig å starte dialogen tidlig for å bygge identiteten til prosjektet og sikre at alle drar i samme retning.</p>
              </BaseCard>
              <BaseCard class="h-full">
                <p class="mb-1 text-lg font-semibold">Fridtjof Denneche</p>
                <p class="mb-3 text-sm text-gray-700">Avdelingsleder regulering, Asker kommune</p>
                <p class="mb-3 text-lg font-semibold leading-relaxed">"For politikerne, som til syvende og sist er beslutningstakerne, gir Bokvalitetsrosen en enkel og visuell sammenstilling av et prosjekt, som kan hjelpe i beslutningsprosessen."</p>
                <p class="text-sm leading-relaxed">Kvalitet er veldig viktig for kommunen, og vi har tydelige føringer for dette i kommuneplanen. Vi vurderer prosjekter opp og ned, og bokvalitet kan være så mangt – et prosjekt kan tilføre kvalitet både for dem som skal bo der og for nabolaget rundt. Vi har alle samme mål; vi vil jo skape de gode prosjektene til slutt.</p>
              </BaseCard>
              <BaseCard class="h-full">
                <p class="mb-1 text-lg font-semibold">Per-Arne Horne</p>
                <p class="mb-3 text-sm text-gray-700">Direktør, Direktoratet for byggkvalitet</p>
                <p class="mb-3 text-lg font-semibold leading-relaxed">"En av styrkene er muligheten til å veie ulike kvaliteter opp mot hverandre, slik at man får gode og åpne diskusjoner."</p>
                <p class="text-sm leading-relaxed">Jeg tror det er et bra verktøy for samhandling. Det er alfa og omega at man med dette verktøyet kan få en større enighet om ting. Jeg er spent på hvordan kommunene kommer til å bruke det – om dialogen skjer direkte gjennom verktøyet, eller som et resultat av verktøyet.</p>
              </BaseCard>
            </div>
          </div>
        </div>
      </section>

      <section id="abonnement-og-priser" class="content-wrapper py-10">
        <h2 class="mb-12 text-3xl font-semibold text-black">Abonnement og priser</h2>
        <p class="mb-6 text-gray-700">
          Velg det abonnementet som passer best for din organisasjon. Medlemspris gjelder for
          medlemmer i Norsk Eiendom eller Arkitektbedriftene. Alle abonnement gir tilgang til de
          samme funksjonene.
        </p>
        <PricingPlans
          :show-description="false"
          :show-starburst="true"
          @select-plan="openModal"
        />
      </section>

      <section id="hvordan-fungerer-bokvalitetsrosen" class="content-wrapper py-10">
        <h2 class="mb-12 text-3xl font-semibold text-black">Hvordan fungerer Bokvalitetsrosen?</h2>
        <div class="grid gap-6 lg:grid-cols-2">
          <div>
            <h3 class="text-xl font-semibold mt-6 mb-3">Slik bruker du Bokvalitetsrosen:</h3>
            <ul class="list-disc pl-5 space-y-2 text-gray-700">
              <li>Opprett prosjekt og legg inn en kort beskrivelse.</li>
              <li>Gjennomfør workshop sammen med arkitekt/utvikler.</li>
              <li>Svar på spørsmål i åtte bokvalitetskategorier og begrunn kort.</li>
              <li>Legg eventuelt til ekstra kvaliteter som ikke fanges opp av spørsmålene.</li>
              <li>Se rosediagrammet som visualiserer styrker og svakere punkter.</li>
              <li>Lag rapport med alle svar og begrunnelser.</li>
              <li>Del rapporten via lenke med kommunen eller andre aktører.</li>
            </ul>
            <h3 class="text-xl font-semibold mt-6 mb-3">Slik foregår dialogen med kommunen:</h3>
            <ul class="list-disc pl-5 space-y-2 text-gray-700">
              <li>Kommunen mottar rapporten via lenke.</li>
              <li>Saksbehandler kan kommentere og stille spørsmål direkte i rapporten.</li>
              <li>Kommentarer lagres og er synlige for arkitekt/utvikler, som kan svare i samme rapport.</li>
              <li>All dialog samles ett sted.</li>
              <li>Ved behov kan dere revidere workshopen og sende oppdatert rapport.</li>
              <li>Eksporter sluttrapporten til PDF og legg den ved plansaken.</li>
            </ul>
          </div>

          <video class="w-full base-card bg-white shadow-md rounded-xl border border-gray-100" controls controlsList="nodownload">
            <source src="Bokvalitetsrosen_Demovideo.mp4#t=0.001" type="video/mp4">
          </video>
        </div>
      </section>

      <section id="historien-bak-bokvalitetsrosen" class="content-wrapper py-10">
        <h2 class="mb-12 text-3xl font-semibold text-black">Historien bak Bokvalitetsrosen</h2>
        <div class="text-gray-700 story-columns">
          <p class="mb-5 story-paragraph">
            Gjennom flere år har både kommuner, arkitekter og boligutviklere kjent på det samme behovet: et verktøy som gjør det mulig å snakke om bokvalitet på en klarere og mer målrettet måte.
          </p>
          <p class="mb-5 story-paragraph">
            I tidligfase er begrepet «god bokvalitet» vanskelig å definere, og diskusjonene har ofte vært preget av subjektive vurderinger, misforståelser og uklare forventninger. Teknisk forskrift setter minstekrav, men fanger ikke nødvendigvis opp de kvalitetene som betyr mest for dem som faktisk skal bo der.
          </p>
          <p class="mb-5 story-paragraph">
            Bokvalitetsrosen er derfor utviklet som et felles, konkret språk for hva god bokvalitet innebærer, og som et hjelpemiddel til å dokumentere, diskutere og formidle bokvalitet på en strukturert måte i planprosesser.
          </p>
          <p class="mb-5 story-paragraph">
            Arbeidet startet i 2020, da Norsk Eiendom identifiserte behovet for et praktisk og lettfattelig verktøy som kunne brukes tidlig i planleggingen. Målet var å redusere konflikter, skape mer effektive dialoger og gjøre det enklere for utviklere, arkitekter og kommunale planavdelinger å forstå hverandre.
          </p>
          <p class="mb-5 story-paragraph">
            SINTEF Community ble engasjert for å kartlegge hvilke kriterier som er viktigst, og hvordan de eventuelt kan måles. For å sikre bransjeforankring og praktisk relevans ble det etablert ressursgrupper med representanter fra Arkitektbedriftene, NAL, OBOS, Huseierne, Norsk Kommunalteknisk Forening, Plan- og bygningsetaten i Oslo, Byantropologene, LPO, AF Eiendom, OSU, Bonava og Birk &amp; Co.
          </p>
          <p class="mb-5 story-paragraph">
            Basert på anbefalingene fra SINTEF Community og innsatsen fra den bredt sammensatte ressursgruppen ble Bokvalitetsrosen utviklet til et verktøy som gjør bokvalitet i boligprosjekter konkret og lett å forstå. LINK Arkitektur har stått for design og programmering av verktøyet.
          </p>
          <p class="mb-5 story-paragraph">
            For boligutviklere gir verktøyet en mulighet til å synliggjøre kvalitetsambisjoner på en mer presis og etterprøvbar måte. For arkitekter bidrar det til å løfte frem sider ved bokvalitet som ellers lett forsvinner i tekniske diskusjoner. For kommuner gir det et bedre og mer nyansert grunnlag for vurdering og dialog – tidlig i prosessene, der de viktigste valgene tas.
          </p>
          <p class="mb-5 story-paragraph">
            I 2025 gikk Arkitektbedriftene inn som medeier, og verktøyet ble videreutviklet som en digital løsning. Hensikten er å gjøre det enklere å snakke samme språk om kvalitet i boligprosjekter og å synliggjøre helheten i et prosjekt på en strukturert, visuell og forståelig måte.
          </p>
          <p class="mb-5 story-paragraph">
            Bokvalitetsrosen bygger på åtte kvalitetsområder som samlet dekker både fysiske, sosiale og opplevelsesmessige forhold – fra utearealer og dagslys til sosiale soner, fleksibilitet og materialbruk. Kriteriene gjør det mulig å vurdere helheten i et prosjekt, ikke bare enkeltkrav. Verktøyet hjelper prosjektteam med å diskutere prioriteringer og se hvordan styrker på et område kan kompensere for svakheter på et annet.
          </p>
          <p class="story-paragraph">
            Høsten 2025 ble Bokvalitetsrosen lansert som et digitalt dialogverktøy. Målet er å heve bokvaliteten i norske boligprosjekter – uavhengig av prosjektets størrelse, beliggenhet eller budsjett – til fordel for dem som skal bo der.
          </p>
        </div>
      </section>

      <footer class="content-wrapper pt-6 pb-12 border-t border-gray-200">
        <p class="text-sm mb-2">
          Kontakt oss på
          <a href="mailto:post@bokvalitetsrosen.no" class="underline">post@bokvalitetsrosen.no</a>
        </p>
        <p class="text-sm text-gray-600 mb-6">
          Bokvalitetsrosen eies og driftes av Norsk Eiendom og Arkitektbedriftene.
        </p>
        <div class="flex flex-wrap items-center gap-10">
          <img src="/img/norsk_logo.png" alt="Norsk Eiendom" class="h-8 w-auto">
          <img src="/img/arkitekt_logo.png" alt="Arkitektbedriftene" class="h-8 w-auto">
        </div>
      </footer>
      <RequestLicenseModal
        v-model="showModal"
        :selected-plan="selectedPlan"
      />
        </main>
      </transition>

      <div class="hidden">{{ version }} / {{ hash }}</div>
    </div>
  </div>
</template>

<script>
import Header from '@/components/shared/header/Header'
import Hero from '@/components/shared/Hero'
import BaseCard from '@/components/shared/BaseCard.vue'
import BaseLoader from '@/components/shared/BaseLoader.vue'
import PricingPlans from '@/components/shared/PricingPlans.vue'
import RequestLicenseModal from '@/components/shared/RequestLicenseModal.vue'
import { isLinkKompassOrigin } from '@/composables/domainUtils'
import { sleep } from '@/composables/utils'
import BokvalitetLandingPageIllustration from '@/assets/images/Bokvalitet_landing_page_illustration.png'

export default {
  components: {
    Header,
    Hero,
    BaseCard,
    BaseLoader,
    PricingPlans,
    RequestLicenseModal,
  },
  data () {
    return {
      showHeader: false,
      showHero: false,
      showContent: false,
      loading: true,
      showModal: false,
      selectedPlan: null,
      version: process.env.VUE_APP_VERSION,
      hash: process.env.VUE_APP_GIT_HASH,
      bokvalitetLandingPageIllustration: BokvalitetLandingPageIllustration,
    }
  },
  methods: {
    openModal (plan) {
      this.selectedPlan = plan
      this.showModal = true
    },
  },
  async mounted () {
    this.loading = false
    this.showHero = true
    await sleep(600)
    this.showHeader = true
    await sleep(250)
    this.showContent = true
  },
  computed: {
    isLink () {
      return isLinkKompassOrigin()
    },
  },
}
</script>

<style scoped>
.story-columns {
  column-width: 22rem;
  column-gap: 2rem;
}

.story-paragraph {
  break-inside: avoid-column;
}
</style>
